import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "sr_cookies_v1";

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const evaluate = () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // tiny extra tick so the slide-in transition triggers cleanly
        setVisible(true);
        requestAnimationFrame(() => setMounted(true));
      }
    };
    const tm = setTimeout(evaluate, 500);
    // Listen for "manage cookie preferences" clicks from the footer
    const onReset = () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch {}
      evaluate();
      // scroll to top so the banner is immediately visible
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    window.addEventListener("cookies:reset", onReset);
    return () => {
      clearTimeout(tm);
      window.removeEventListener("cookies:reset", onReset);
    };
  }, []);

  const save = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    setMounted(false);
    setTimeout(() => setVisible(false), 250);
  };

  // While the banner is visible, push the page bottom up by ~140px so the
  // banner never covers the legal footer rows (REA, ODR, etc.).
  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "160px";
    return () => { document.body.style.paddingBottom = prev; };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t("cookies.title")}
      data-testid="cookie-banner"
      className={[
        "fixed left-0 right-0 bottom-0 z-[60]",
        "bg-zinc-950/85 backdrop-blur-2xl",
        "border-t border-white/[0.06]",
        "shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.6)]",
        "transition-transform duration-300 ease-out",
        mounted ? "translate-y-0" : "translate-y-full",
      ].join(" ")}
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
    >
      {/* Brand accent line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#FFD700]/60 to-transparent" />

      <div className="max-w-5xl mx-auto px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Icon + text */}
        <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 shrink-0 rounded-lg bg-[#FFD700]/10 text-[#FFD700] grid place-items-center ring-1 ring-[#FFD700]/30">
            <Cookie className="w-4 h-4" />
          </div>
          <p className="text-[13px] leading-snug text-zinc-300 min-w-0">
            <span className="font-semibold text-zinc-100">{t("cookies.title")}</span>
            <span className="hidden sm:inline text-zinc-500"> · </span>
            <span className="block sm:inline text-zinc-400">
              {t("cookies.short_body", { defaultValue: t("cookies.body") })}
            </span>{" "}
            <Link
              to="/privacy-policy"
              className="text-[#FFD700]/90 hover:text-[#FFD700] underline underline-offset-2 decoration-[#FFD700]/40"
              data-testid="cookie-more-link"
            >
              {t("cookies.more")}
            </Link>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => save("reject")}
            className="px-4 py-2 rounded-lg text-xs font-medium text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/70 transition"
            data-testid="cookie-reject"
          >
            {t("cookies.reject")}
          </button>
          <button
            onClick={() => save("accept_all")}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#FFD700] text-zinc-950 hover:brightness-110 active:scale-[0.98] transition"
            data-testid="cookie-accept-all"
          >
            {t("cookies.accept_all")}
          </button>
        </div>
      </div>
    </div>
  );
}
